import React from 'react';
import { X, Pencil, AlertTriangle } from 'lucide-react';
import { StreamRenameResult } from '../services/api';
import './StreamRenameConfirmModal.css';

interface StreamRenameConfirmModalProps {
  displayName: string;
  preview: StreamRenameResult | null; // null = still loading
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Pre-write impact confirmation modal shown after the preview call returns.
 * Mirrors GenerateConfirmModal.tsx's actual established in-catalogue modal
 * pattern (plain overlay/modal divs, own CSS file, CSS-variable colours) —
 * .srm-* prefix parallels that component's .gcm-* prefix.
 */
const StreamRenameConfirmModal: React.FC<StreamRenameConfirmModalProps> = ({
  displayName, preview, busy, onConfirm, onCancel,
}) => {
  const isLoading = preview === null;

  return (
    <div className="srm-overlay" onClick={busy ? undefined : onCancel}>
      <div className="srm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="srm-header">
          <h3 className="srm-title">Rename stream</h3>
          <button className="srm-close" onClick={onCancel} disabled={busy} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="srm-summary">
          {isLoading ? (
            <span className="srm-summary-loading">Calculating…</span>
          ) : (
            <>
              <p className="srm-summary-text">
                Rename to <strong>{displayName}</strong>? This updates{' '}
                <strong>{preview.tasks_updated} article{preview.tasks_updated !== 1 ? 's' : ''}</strong>
                {preview.masterplan_document_updated && <>, your masterplan</>}, and your stream list.
                It can't be undone automatically.
              </p>
              {!preview.masterplan_document_updated && (
                <p className="srm-summary-note">
                  This stream isn't in your masterplan document, so future masterplan generation is unaffected.
                </p>
              )}
              {preview.untracked_mentions > 0 && (
                <p className="srm-summary-warning">
                  <AlertTriangle size={13} />
                  The old name still appears {preview.untracked_mentions} time(s) elsewhere in your
                  masterplan (competitor analysis, keyword rationale). These will be left unchanged.
                </p>
              )}
            </>
          )}
        </div>

        <div className="srm-footer">
          <button className="srm-btn-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="srm-btn-confirm"
            onClick={onConfirm}
            disabled={isLoading || busy}
          >
            <Pencil size={13} />
            {busy ? 'Renaming…' : 'Rename'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StreamRenameConfirmModal;
